/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, TVocabularyBookCategory, TVocabularyBookLevel } from '@datatype/iyagigo/api-type';
import { IAdminVocabularyBookData } from '@datatype/admin/api-type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as adminActions } from '@store/slice/admin-slice';
import { useAdminVocabularyBooksQuery, useAssembleAdminVocabularyBookMutation } from '@hook/admin/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import VocabularyBookFilter from '@component/admin/vocabulary-book/vocabulary-book-home/vocabulary-book-filter';
import VocabularyBookList from '@component/admin/vocabulary-book/vocabulary-book-home/vocabulary-book-list';
import VocabularyBookPreview from '@component/admin/vocabulary-book/vocabulary-book-home/vocabulary-book-preview';
import VocabularyBookMetricPanel from '@component/admin/vocabulary-book/vocabulary-book-home/vocabulary-book-metric-panel';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import TextButton from '@design-system/text-button';

const PER_PAGE = 20;
// 백엔드 Constant.VOCABULARY_BOOK_BUNDLE_SIZE 와 동일해야 함 (안내 문구 표시용)
const BUNDLE_SIZE = 10;

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

const MetricPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`;

const CenterPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 600px;
  height: 100%;
  overflow-y: auto;
`;

const RightPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  flex: 1;
  height: 100%;
  overflow-y: auto;
`;

const FilterRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${color.MODAL_COLOR};
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 380px;
  background-color: ${color.WHITE_1};
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${color.SURFACE_SHADOW};
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
`;

const Notice = styled.div`
  background-color: ${color.BROWN_4};
  color: ${color.BROWN_1};
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
`;

const VocabularyBookHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const selectedBookKey =
    useTypedSelector((state) => state.rootReducer?.adminReducer?.selectedVocabularyBookKey) ?? null;
  const filter = useTypedSelector(
    (state) =>
      state.rootReducer?.adminReducer?.vocabularyBookFilter ?? {
        languageCode: null,
        category: null,
        levels: null,
        published: null,
      },
  );
  const { languageCode, category, levels, published } = filter;

  const centerPaneRef = useRef<HTMLDivElement | null>(null);

  const booksQuery = useAdminVocabularyBooksQuery({
    perPage: PER_PAGE,
    languageCode,
    category,
    levels,
    published,
  });

  const books = booksQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [];

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: booksQuery.hasNextPage ?? false,
    isFetchingNextPage: booksQuery.isFetchingNextPage ?? false,
    fetchNextPage: booksQuery.fetchNextPage,
    root: centerPaneRef,
  });

  const selectedBook: IAdminVocabularyBookData | null = useMemo(() => {
    if (!selectedBookKey) return null;
    return books.find((b) => b.key === selectedBookKey) ?? null;
  }, [books, selectedBookKey]);

  const handleLanguageChange = (lc: EApiLanguageCode | null) => {
    dispatch(adminActions.storeVocabularyBookFilter({ languageCode: lc }));
  };
  const handleCategoryChange = (c: TVocabularyBookCategory | null) => {
    dispatch(adminActions.storeVocabularyBookFilter({ category: c }));
  };
  const handleLevelsChange = (next: TVocabularyBookLevel[] | null) => {
    dispatch(adminActions.storeVocabularyBookFilter({ levels: next }));
  };
  const handlePublishedChange = (p: boolean | null) => {
    dispatch(adminActions.storeVocabularyBookFilter({ published: p }));
  };
  const handleSelect = (book: IAdminVocabularyBookData) => {
    dispatch(adminActions.storeSelectedVocabularyBookKey(book.key));
  };

  const [isAssembleModalOpen, setIsAssembleModalOpen] = useState<boolean>(false);
  const [isAssembleRequested, setIsAssembleRequested] = useState<boolean>(false);
  const assembleMutation = useAssembleAdminVocabularyBookMutation();

  const handleAssembleOpen = () => {
    setIsAssembleRequested(false);
    setIsAssembleModalOpen(true);
  };
  const handleAssembleClose = () => {
    setIsAssembleModalOpen(false);
  };
  const handleAssembleSubmit = () => {
    // fire-and-forget — 묶음 조립은 모든 (언어×카테고리×레벨) 순회라 오래 걸림. 응답 안 기다리고 "요청되었습니다" 전환.
    assembleMutation.mutate();
    setIsAssembleRequested(true);
  };

  return (
    <Wrapper>
      <MetricPane>
        <VocabularyBookMetricPanel />
      </MetricPane>
      <CenterPane ref={centerPaneRef}>
        <FilterRow>
          <VocabularyBookFilter
            languageCode={languageCode}
            category={category}
            levels={levels}
            published={published}
            handleLanguageChange={handleLanguageChange}
            handleCategoryChange={handleCategoryChange}
            handleLevelsChange={handleLevelsChange}
            handlePublishedChange={handlePublishedChange}
          />
          <TextButton text='단어 묶기' width='100px' handleClick={handleAssembleOpen} />
        </FilterRow>
        <VocabularyBookList
          books={books}
          selectedBookKey={selectedBookKey}
          isLoading={booksQuery.isLoading}
          isFetchingNextPage={booksQuery.isFetchingNextPage ?? false}
          isError={booksQuery.isError}
          handleSelect={handleSelect}
          handleRetry={booksQuery.refetch}
          sentinelRef={sentinelRef}
        />
      </CenterPane>
      <RightPane>
        <VocabularyBookPreview selectedBook={selectedBook} />
      </RightPane>
      {isAssembleModalOpen && (
        <Backdrop onClick={handleAssembleClose}>
          <Card onClick={(ev) => ev.stopPropagation()}>
            <CloseButton>
              <IconBlock
                icon='close'
                design={{
                  icon: { size: 'medium', color: color.BLACK_4 },
                  box: { event: { cursor: 'pointer' } },
                }}
                click={{ handler: handleAssembleClose }}
              />
            </CloseButton>
            {isAssembleRequested ? (
              <>
                <TextBlock
                  text='요청되었습니다'
                  design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
                />
                <Notice>
                  단어 묶기 작업이 백그라운드로 진행 중입니다. 모든 (언어 × 카테고리 × 레벨) 조합을 순회하므로 잠시 시간이 걸려요. 결과는 잠시 후 목록을 새로고침해서 확인하세요.
                </Notice>
              </>
            ) : (
              <>
                <TextBlock
                  text='단어를 묶으시겠어요?'
                  design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
                />
                <Notice>
                  {`미편입 ai_voca를 ${BUNDLE_SIZE}개씩 묶어 단어집을 자동 생성하고 즉시 발행합니다.\n모든 (언어 × 카테고리 × 레벨) 조합을 순회하며, 묶음에 ${BUNDLE_SIZE}개를 못 채우는 조합은 다음 사이클까지 보류돼요.`}
                </Notice>
                <ButtonRow>
                  <TextButton
                    text='아니오'
                    theme={ETheme.GRAY}
                    kind='outlined'
                    width='100px'
                    handleClick={handleAssembleClose}
                  />
                  <TextButton text='예' width='100px' handleClick={handleAssembleSubmit} />
                </ButtonRow>
              </>
            )}
          </Card>
        </Backdrop>
      )}
    </Wrapper>
  );
};

export default VocabularyBookHome;
